import React, { useState, useEffect } from 'react';

import { getLabelFromName } from '../../helpers/formUtils';

import QuestionMark from './QuestionMark';

export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc',
    OFF = '',
}

export type SortParams = { [column: string]: SortOrder };

export type SortProps = {
    sortParams: SortParams;
    onSortChange: (column: string, order: SortOrder) => void;
};

type Props = {
    column: string;
    label?: string;
    children?: React.ReactNode;
    definition?: string;
    order?: 'asc' | 'desc';
} & SortProps;

export default function SortButton({ column, label, onSortChange, sortParams, children, definition, order }: Props) {
    const [innerOrder, setInnerOrder] = useState<SortOrder>(sortParams[column] ?? SortOrder.OFF);
    const isSortEnabled = innerOrder !== SortOrder.OFF;
    const isSortAsc = innerOrder === SortOrder.ASC;

    function handleClick(newOrder: SortOrder) {
        setInnerOrder(newOrder);
        onSortChange(column, newOrder);
    }

    useEffect(() => {
        if (order) {
            handleClick(order === 'asc' ? SortOrder.ASC : SortOrder.DESC);
        }
    }, [order]);

    return (
        <div className="sort-header-container" data-column={column}>
            <button
                className="sort-header-column-button"
                type="button"
                onClick={() => handleClick(isSortEnabled ? SortOrder.OFF : SortOrder.ASC)}
                title={isSortEnabled ? 'Clear' : 'Sort ascending'}
            >
                {children ?? label ?? getLabelFromName(column)}
                {definition ? <QuestionMark definition={definition} /> : ''}
            </button>
            <span className="sort-header-order-container">
                {isSortEnabled && (
                    <button
                        className="sort-header-order-button"
                        type="button"
                        onClick={() => handleClick(isSortAsc ? SortOrder.DESC : SortOrder.ASC)}
                        title={`Sort ${isSortAsc ? 'descending' : 'ascending'}`}
                    >
                        {isSortAsc ? '\u25B2' : '\u25BC'}
                    </button>
                )}
            </span>
        </div>
    );
}
